import { useAppSelector } from '../../app/state';
import { callAction } from '../../app/actions';
import type { FavTargetType } from '../../api/me';

/**
 * The watch bell (plan Phase 3) — `FavStar`'s sibling, designed as a pair with it: same
 * button family (`.favbtn`), same two homes (the node popup's header, the doc overlay's
 * bar), always rendered immediately AFTER the star so the pair reads star-then-bell
 * everywhere. Outline = not watching; filled = watching, in the accent colour so the two
 * "on" states (gold star, blue bell) stay tellable at a glance.
 *
 * Subscribes to exactly one boolean, exactly as the star does, and carries no state of its
 * own: `toggleSubscription` (optimistic + rollback) owns the truth, and what the snapshot
 * says is what the bell shows.
 *
 * Hidden while signed out and until the subscription list has loaded — a toggle whose truth
 * is not yet known invites double writes, and one that flips a beat after appearing looks
 * broken. The same two gates `FavStar` uses.
 */
export function WatchToggle({
  targetType,
  targetId,
  id,
  px = 17,
}: {
  targetType: FavTargetType;
  targetId: string;
  /** DOM id, so a spec can address one bell among several. */
  id?: string;
  px?: number;
}) {
  const ready = useAppSelector((s) => !!s.currentUser && s.subscriptionsLoaded);
  const on = useAppSelector((s) => s.subscriptions.has(`${targetType}:${targetId}`));
  if (!ready) return null;
  return (
    <button
      type="button"
      id={id}
      className={on ? 'favbtn watch on' : 'favbtn watch'}
      title={on ? 'Stop watching — no more change notifications' : 'Watch — get notified when this changes'}
      aria-pressed={on ? 'true' : 'false'}
      onClick={(e) => {
        e.stopPropagation();
        callAction('toggleSubscription', targetType, targetId);
      }}
    >
      <svg className="ico" style={{ width: px, height: px }}>
        <use href={on ? '#i-bell-fill' : '#i-bell'} />
      </svg>
    </button>
  );
}
